import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

const TOOLTIP_Z_INDEX = 3000;
const GAP_PX = 8;
const VIEWPORT_MARGIN_PX = 8;
const ENTER_DELAY_MS = 300;
const LEAVE_DELAY_MS = 100;

/**
 * Shared portaled tooltip behavior for AI chat triggers: enter/leave delays,
 * viewport-safe positioning, and scroll/resize repositioning.
 *
 * @param {{
 *   placement?: 'top' | 'auto',
 *   clampToViewport?: boolean,
 * }} [options]
 */
export function usePortaledTooltip(options = {}) {
  const { placement = 'top', clampToViewport = false } = options;
  const useAutoPlacement = placement === 'auto' || clampToViewport;

  const tooltipId = useId();
  const triggerRef = useRef(null);
  const popperRef = useRef(null);
  const enterTimerRef = useRef(null);
  const leaveTimerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, placement: 'top' });

  const clearTimers = useCallback(() => {
    if (enterTimerRef.current) {
      clearTimeout(enterTimerRef.current);
      enterTimerRef.current = null;
    }
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
  }, []);

  const scheduleShow = useCallback(() => {
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
    if (open || enterTimerRef.current) {
      return;
    }
    enterTimerRef.current = setTimeout(() => {
      enterTimerRef.current = null;
      setOpen(true);
    }, ENTER_DELAY_MS);
  }, [open]);

  const scheduleHide = useCallback(() => {
    if (enterTimerRef.current) {
      clearTimeout(enterTimerRef.current);
      enterTimerRef.current = null;
    }
    if (!open || leaveTimerRef.current) {
      return;
    }
    leaveTimerRef.current = setTimeout(() => {
      leaveTimerRef.current = null;
      setVisible(false);
      setOpen(false);
    }, LEAVE_DELAY_MS);
  }, [open]);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) {
      return;
    }
    const rect = trigger.getBoundingClientRect();

    if (useAutoPlacement) {
      const popper = popperRef.current;
      const popperWidth = popper ? popper.offsetWidth : 0;
      const popperHeight = popper ? popper.offsetHeight : 0;
      const viewportWidth = window.innerWidth;
      const centerX = rect.left + rect.width / 2;

      const halfWidth = popperWidth / 2;
      const minLeft = VIEWPORT_MARGIN_PX + halfWidth;
      const maxLeft = viewportWidth - VIEWPORT_MARGIN_PX - halfWidth;
      const left =
        maxLeft >= minLeft
          ? Math.min(Math.max(centerX, minLeft), maxLeft)
          : centerX;

      const hasRoomAbove =
        rect.top - GAP_PX - popperHeight >= VIEWPORT_MARGIN_PX;
      const resolvedPlacement = hasRoomAbove ? 'top' : 'bottom';
      const top =
        resolvedPlacement === 'top' ? rect.top - GAP_PX : rect.bottom + GAP_PX;

      setCoords({ top, left, placement: resolvedPlacement });
      return;
    }

    if (placement === 'top') {
      setCoords({
        top: rect.top - GAP_PX,
        left: rect.left + rect.width / 2,
        placement: 'top',
      });
    }
  }, [placement, useAutoPlacement]);

  useLayoutEffect(() => {
    if (!open) {
      setVisible(false);
      return undefined;
    }
    updatePosition();
    const rafId = requestAnimationFrame(() => {
      updatePosition();
      setVisible(true);
    });
    const onReposition = () => updatePosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, updatePosition]);

  useEffect(() => clearTimers, [clearTimers]);

  const renderPopper = useCallback(
    (content) => {
      if (!open || typeof document === 'undefined') {
        return null;
      }
      const popperPlacement = coords.placement || placement;
      return createPortal(
        <div
          ref={popperRef}
          id={tooltipId}
          role="tooltip"
          className={[
            'ddv-ai-chat-tooltip__popper',
            visible ? 'ddv-ai-chat-tooltip__popper--visible' : '',
            `ddv-ai-chat-tooltip__popper--${popperPlacement}`,
          ]
            .filter(Boolean)
            .join(' ')}
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            zIndex: TOOLTIP_Z_INDEX,
          }}
        >
          {content}
        </div>,
        document.body,
      );
    },
    [coords, open, placement, tooltipId, visible],
  );

  return {
    tooltipId,
    triggerRef,
    open,
    scheduleShow,
    scheduleHide,
    renderPopper,
  };
}
